// Login.component: Login page OAuth brand rows plus email-open row with left envelope icon (inline SVG).
// Mistake: email login CTA had text only; align icon+label layout with OAuth buttons for visual consistency.
// Mistake: LINE login used generic social-redirect; route LINE through LIFF URL/ID from env for stable in-app callback.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import i18n from "i18next";

import { setInfo as setUserInfo, setIsLogin } from "reducers/User";

import { checkIsEmail } from "helpers/Reg.helper";
import { removeAgencyId, setAccessToken, setRefreshToken } from "helpers/Auth.helper";
import { getUserType } from "helpers/Common.helper";

import { getMyInfo, login, saveAppPushInfo, setPushActivate } from "api/common/Auth.api";
import * as AgencyApi from "api/agency/Agency.api";
import * as SchoolApi from "api/school/School.api";
import * as StudentApi from "api/student/Student.api";
import * as KoachApi from "api/koach/Koach.api";
import { requestNotificationPermission } from "helpers/Firebase.helper";
import { setAppAccessToken, setAppRefreshToken } from "helpers/appLocalStorageHelper";
import { executeAppleOAuth } from "helpers/AppleOauth.helper";
import { checkHasPushPermission, getOsType, isAppWebViewContextQuick, isInApp } from "helpers/InApp.helper";
import { requestNativeSocialLogin } from "helpers/SocialBridge.helper";
import AuthHeader from "../component/AuthHeader.component";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import * as CookieHelper from "helpers/Cookie.helper";
import { IUserProperties, setUserProperties } from "helpers/Gtm.helper";
import { getCurrentLanguageCode } from "helpers/String.helper";

const loginEmailOpenButtonClass =
    "flex w-full items-center gap-3 rounded-xl border-2 border-transparent bg-[#CF3688] py-3.5 pl-3 pr-4 text-base font-semibold text-white shadow-md transition hover:bg-[#A62B6D] focus:outline-none focus:ring-2 focus:ring-[#CF3688]/40";

const LoginEmailEnvelopeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-6 w-6 shrink-0" fill="currentColor" aria-hidden>
        <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z" />
    </svg>
);

type LoginOauthProviderId = "google" | "line" | "kakao" | "apple";

const OAUTH_BRAND: Record<
    LoginOauthProviderId,
    { logoSrc: string; buttonClass: string; iconWrapClass: string; imgClass: string }
> = {
    google: {
        logoSrc: "/imgs/common/logo/icon-google-logo.svg",
        buttonClass: "bg-[#4285F4] text-white hover:bg-[#3367D6] focus:ring-[#4285F4]/50",
        iconWrapClass: "bg-white",
        imgClass: "h-6 w-6",
    },
    line: {
        logoSrc: "/imgs/common/logo/icon-line-logo.svg",
        buttonClass: "bg-[#06C755] text-white hover:bg-[#05b34c] focus:ring-[#06C755]/50",
        iconWrapClass: "bg-transparent",
        imgClass: "h-7 w-7",
    },
    kakao: {
        logoSrc: "/imgs/common/logo/icon-kakao-logo.svg",
        buttonClass: "bg-[#FEE500] text-[#191919] hover:bg-[#f5dc00] focus:ring-[#FEE500]/60",
        iconWrapClass: "bg-transparent",
        imgClass: "h-6 w-6",
    },
    apple: {
        logoSrc: "/imgs/common/logo/icon-apple-logo-w.svg",
        buttonClass: "bg-black text-white hover:bg-neutral-900 focus:ring-white/30",
        iconWrapClass: "bg-transparent",
        imgClass: "h-6 w-6",
    },
};

const LoginOauthBrandButton = (props: { provider: LoginOauthProviderId; label: string; onClick: () => void }) => {
    const { provider, label, onClick } = props;
    const b = OAUTH_BRAND[provider];
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            className={`flex w-full items-center gap-3 rounded-xl py-3.5 pl-3 pr-4 text-base font-semibold shadow-md transition focus:outline-none focus:ring-2 ${b.buttonClass}`}
        >
            <span
                className={`flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg ${b.iconWrapClass}`}
            >
                <img src={b.logoSrc} alt="" className={b.imgClass} />
            </span>
            <span className="min-w-0 flex-1 text-center">{label}</span>
            <span className="h-10 w-10 shrink-0" aria-hidden />
        </button>
    );
};

const LoginComponent = (props: any) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const authBase = (process.env.REACT_APP_AUTH_URL || "").replace(/\/+$/, "");
    const goStudentOAuth = async (provider: "google" | "line" | "kakao" | "apple") => {
        if (provider === "apple") {
            await executeAppleOAuth({
                flow: "login",
                authBase,
                locationSearch: location.search || "",
                onNativeBridgeError: (message: string) => {
                    alert(message || getLanguageValue(PAGE_TYPE.COMMON, "studentSignupPre.oauthNotReady"));
                },
            });
            return;
        }
        const result = await requestNativeSocialLogin(provider, "login");
        if (result) {
            if (result && !result.ok) {
                if (result.errorCode === "CANCELLED") {
                    return;
                }
                alert(result.message || getLanguageValue(PAGE_TYPE.COMMON, "studentSignupPre.oauthNotReady"));
            }
            return;
        }
        if (await isAppWebViewContextQuick()) {
            return;
        }

        if (provider === "line") {
            navigate({ pathname: "/line/liff/callback", search: `${location.search || ""}${location.search ? "&" : "?"}flow=login` }, { replace: true });
            return;
        }
        if (!authBase) {
            alert(getLanguageValue(PAGE_TYPE.COMMON, "studentSignupPre.oauthNotReady"));
            return;
        }
        const q = location.search || "";
        window.location.href = `${authBase}/auth/social-redirect/${provider}${q}`;
    };
    const idRef = useRef<HTMLInputElement>(null);
    const pwRef = useRef<HTMLInputElement>(null);
    const { isPrev, path, backUrl } = props;
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [isRememberEmail, setIsRememberEmail] = useState(true);
    const [emailModalOpen, setEmailModalOpen] = useState(false);

    const doLogin = async () => {
        setIsButtonDisabled(true);
        const username: string = idRef?.current?.value || "";
        const password: string = pwRef?.current?.value || "";

        if (checkIsEmail(username)) {
            const result = await login({ username, password });
            console.log({ result });
            if (!result.errorCode) {
                const loginPayload = result?.data?.data;
                setAccessToken(loginPayload?.jwt);
                setRefreshToken(loginPayload?.refreshToken);
                CookieHelper.setLastLoginEmail(username);

                if (isRememberEmail) {
                    console.log("setRememberEmail", username);
                    CookieHelper.setRememberEmail(username);
                    CookieHelper.setIsRememberEmail(true);
                } else {
                    console.log("removeRememberEmail", username);
                    CookieHelper.removeRememberEmail();
                    CookieHelper.removeIsRememberEmail();
                }

                if (loginPayload?.needsStudentProfileCompletion) {
                    dispatch(setIsLogin(true));
                    dispatch(
                        setUserInfo({
                            accountTypeCode: "accountType:student",
                            hasDoneSignup: false,
                        } as any)
                    );
                    setEmailModalOpen(false);
                    navigate("/student/signup", { replace: true });
                    setIsButtonDisabled(false);
                    return;
                }

                try {
                    const myInfoResult = await getMyInfo();

                    if (myInfoResult) {
                        console.log({ myInfoResult });
                        const myInfo = myInfoResult.info;

                        let userType = "";
                        let userTypeId = 0;
                        if (myInfoResult.info.accountTypeCode === "accountType:student") {
                            if (myInfoResult.info.hasDoneSignup === false) {
                                dispatch(setIsLogin(true));
                                dispatch(setUserInfo(myInfoResult.info));
                                setEmailModalOpen(false);
                                navigate("/student/signup", { replace: true });
                                setIsButtonDisabled(false);
                                return;
                            }
                            const result = await StudentApi.getInfoForStudent();
                            if (result) {
                                myInfoResult.info.profileImage = result.info.profileImageInfo ? result.info.profileImageInfo.url : null;
                            } else {
                                myInfoResult.info.profileImage = null;
                            }
                            userType = "student";
                            userTypeId = myInfoResult.info.userStudentId;
                        }

                        if (myInfoResult.info.accountTypeCode === "accountType:agency") {
                            removeAgencyId();

                            console.log(myInfoResult);

                            const result = await AgencyApi.getInfo(myInfoResult.info.userAgencyId);
                            if (result) {
                                myInfoResult.info.logoImageInfo = result.info.logoImageInfo ? result.info.logoImageInfo : null;
                                myInfoResult.info.agencyName = result.info.agencyName;
                            } else {
                                myInfoResult.info.logoImageInfo = null;
                            }
                            userType = "agency";
                            userTypeId = myInfoResult.info.userAgencyId;
                        }

                        if (myInfoResult.info.accountTypeCode === "accountType:school") {
                            const result = await SchoolApi.getInfo(myInfoResult.info.userSchoolId);
                            if (result) {
                                myInfoResult.info.logoImageInfo = result.schoolInfo.logoImageInfo ? result.schoolInfo.logoImageInfo : null;
                            } else {
                                myInfoResult.info.logoImageInfo = null;
                            }
                            userType = "school";
                            userTypeId = myInfoResult.info.userSchoolId;
                        }

                        if (myInfoResult.info.accountTypeCode === "accountType:koach") {
                            const result = await KoachApi.getInfoForKoach();
                            if (result) {
                                myInfoResult.info.profileImage = result.info.profileImageInfo ? result.info.profileImageInfo.url : null;
                            } else {
                                myInfoResult.info.profileImage = null;
                            }
                            userType = "koach";
                            userTypeId = myInfoResult.info.userId;
                        }

                        dispatch(setIsLogin(true));
                        dispatch(setUserInfo(myInfo));

                        const osType = await getOsType();

                        const userProperties: IUserProperties = {
                            user_type: userType,
                            user_type_id: userTypeId,
                            preferred_language: getCurrentLanguageCode(),
                            device_type: null,
                            device_os: null,
                            device_os_version: null,
                        };

                        const isApp = await isInApp();
                        if (isApp) {
                            userProperties.device_type = osType;
                        } else {
                            userProperties.device_type = "web";
                        }

                        console.log({ userProperties });
                        setUserProperties(userProperties);

                        if (!isApp) {
                            await requestNotificationPermission();
                        } else {
                            await saveAppPushInfo();
                            const hasPushPermission = await checkHasPushPermission();
                            await setPushActivate({ isActivated: hasPushPermission });
                            await setAppAccessToken(result?.data?.data?.jwt);
                            await setAppRefreshToken(result?.data?.data?.refreshToken);
                        }

                        let nextUrl = "";
                        if (location.state?.nextUrl) {
                            nextUrl = location.state.nextUrl;
                        } else {
                            if (myInfo?.accountTypeCode === "accountType:agency") {
                                nextUrl = `/agency`;
                            } else {
                                nextUrl = `/${getUserType(myInfo?.accountTypeCode)}`;
                            }

                            if (getUserType(myInfo?.accountTypeCode) === "agency") {
                                const lang = i18n.language === "ko" ? "ko" : "en";
                                i18n.changeLanguage(lang);
                            }
                        }

                        if (isRememberEmail) {
                            CookieHelper.setRememberEmail(username);
                        } else {
                            CookieHelper.removeRememberEmail();
                        }

                        setEmailModalOpen(false);
                        if (isPrev) {
                            navigate(path, { replace: true });
                        } else {
                            navigate(nextUrl, { replace: true });
                        }
                    }
                } catch (error) {
                    console.error("Failed to get my info:", error);
                } finally {
                    setIsButtonDisabled(false);
                }
            }
        }
        setIsButtonDisabled(false);
    };

    const syncRememberEmailFromCookie = useCallback(() => {
        const remember = CookieHelper.getIsRememberEmail();
        setIsRememberEmail(remember);
        if (remember) {
            const em = CookieHelper.getRememberEmail();
            if (em && idRef.current) {
                idRef.current.value = em;
            }
        }
    }, []);

    useEffect(() => {
        if (!emailModalOpen) {
            return;
        }
        syncRememberEmailFromCookie();
        const t = window.setTimeout(() => {
            if (idRef.current?.value) {
                pwRef.current?.focus();
            } else {
                idRef.current?.focus();
            }
        }, 0);
        return () => window.clearTimeout(t);
    }, [emailModalOpen, syncRememberEmailFromCookie]);

    useEffect(() => {
        if (!emailModalOpen) {
            return;
        }
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setEmailModalOpen(false);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [emailModalOpen]);

    const onChangeRememberEmail = (checked: boolean) => {
        console.log("onChangeRememberEmail", checked);
        setIsRememberEmail(checked);
    };

    return (
        <>
            <AuthHeader backUrl={backUrl ? backUrl : ""} />
            <main className="min-h-screen bg-gray-50 px-4 pb-12 pt-10 sm:px-6 lg:px-8">
                <div className="mx-auto w-full max-w-4xl pt-6 sm:pt-6 lg:pt-6">
                    <div className="mx-auto flex w-full max-w-md flex-col items-center">
                        <div className="mb-8 text-center">
                            <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                                {getLanguageValue(PAGE_TYPE.COMMON, "login.txtTitle")}
                            </h1>
                        </div>

                        <div className="flex w-full flex-col gap-3">
                            <button type="button" className={loginEmailOpenButtonClass} onClick={() => setEmailModalOpen(true)}>
                                <span className="flex h-10 w-10 shrink-0 items-center justify-center text-white" aria-hidden>
                                    <LoginEmailEnvelopeIcon />
                                </span>
                                <span className="min-w-0 flex-1 text-center">
                                    {getLanguageValue(PAGE_TYPE.COMMON, "login.btnEmailLogin")}
                                </span>
                                <span className="h-10 w-10 shrink-0" aria-hidden />
                            </button>
                            <LoginOauthBrandButton
                                provider="google"
                                label={getLanguageValue(PAGE_TYPE.COMMON, "login.btnLoginGoogle")}
                                onClick={() => {
                                    void goStudentOAuth("google");
                                }}
                            />
                            <LoginOauthBrandButton
                                provider="line"
                                label={getLanguageValue(PAGE_TYPE.COMMON, "login.btnLoginLine")}
                                onClick={() => {
                                    void goStudentOAuth("line");
                                }}
                            />
                            <LoginOauthBrandButton
                                provider="kakao"
                                label={getLanguageValue(PAGE_TYPE.COMMON, "login.btnLoginKakao")}
                                onClick={() => {
                                    void goStudentOAuth("kakao");
                                }}
                            />
                            <LoginOauthBrandButton
                                provider="apple"
                                label={getLanguageValue(PAGE_TYPE.COMMON, "login.btnLoginApple")}
                                onClick={() => {
                                    void goStudentOAuth("apple");
                                }}
                            />
                        </div>

                        <div className="mt-8 w-full border-t border-gray-200 pt-6 text-center">
                            <p className="text-sm text-gray-600 sm:text-base">
                                {getLanguageValue(PAGE_TYPE.COMMON, "login.txtSignup")}
                                <br />
                                <button
                                    type="button"
                                    onClick={() => navigate("/signup")}
                                    className="font-semibold text-[#CF3688] transition-colors duration-200 hover:text-[#A62B6D]"
                                >
                                    {getLanguageValue(PAGE_TYPE.COMMON, "login.btnSignup")}
                                </button>
                            </p>
                        </div>
                    </div>
                </div>
            </main>

            {emailModalOpen ? (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="login-email-modal-title"
                    onClick={() => setEmailModalOpen(false)}
                >
                    <div
                        className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="mb-4 flex items-start justify-between gap-3">
                            <h3 id="login-email-modal-title" className="text-lg font-semibold text-gray-900">
                                {getLanguageValue(PAGE_TYPE.COMMON, "login.sectionEmailLogin")}
                            </h3>
                            <button
                                type="button"
                                onClick={() => setEmailModalOpen(false)}
                                className="shrink-0 rounded-lg px-2 py-1 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                            >
                                {getLanguageValue(PAGE_TYPE.COMMON, "login.btnCloseEmailModal")}
                            </button>
                        </div>

                        <form className="w-full space-y-6" onSubmit={(e) => e.preventDefault()}>
                            <div className="space-y-4">
                                <div className="relative">
                                    <input
                                        type="email"
                                        placeholder={getLanguageValue(PAGE_TYPE.COMMON, "login.txtEmailPlaceholder")}
                                        autoComplete="username"
                                        ref={idRef}
                                        onKeyDown={(evt) => {
                                            if (evt.key === "Enter") doLogin();
                                        }}
                                        className="w-full rounded-lg border-2 border-gray-200 px-4 py-3 text-base text-gray-900 transition-all duration-200 focus:border-[#CF3688] focus:ring-2 focus:ring-[#CF3688]/20 sm:text-lg"
                                    />
                                </div>
                                <div className="relative">
                                    <input
                                        type="password"
                                        placeholder={getLanguageValue(PAGE_TYPE.COMMON, "login.txtPasswordPlaceholder")}
                                        ref={pwRef}
                                        onKeyDown={(evt) => {
                                            if (evt.key === "Enter") doLogin();
                                        }}
                                        className="w-full rounded-lg border-2 border-gray-200 px-4 py-3 text-base text-gray-900 transition-all duration-200 focus:border-[#CF3688] focus:ring-2 focus:ring-[#CF3688]/20 sm:text-lg"
                                    />
                                </div>
                                <div className="relative flex items-center">
                                    <input
                                        checked={isRememberEmail}
                                        onChange={(e) => onChangeRememberEmail(e.target.checked)}
                                        id="login-modal-remember"
                                        name="remember"
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-500"
                                    />
                                    <label htmlFor="login-modal-remember" className="ml-2 block text-sm text-gray-700">
                                        {getLanguageValue(PAGE_TYPE.COMMON, "login.rememberEmail")}
                                    </label>
                                </div>
                            </div>

                            <div className="flex items-center justify-end">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setEmailModalOpen(false);
                                        navigate("/password/reset");
                                    }}
                                    className="text-sm font-medium text-[#CF3688] transition-colors duration-200 hover:text-[#A62B6D] sm:text-base"
                                >
                                    {getLanguageValue(PAGE_TYPE.COMMON, "login.btnResetPassword")}
                                </button>
                            </div>

                            <div>
                                <button
                                    disabled={isButtonDisabled}
                                    type="button"
                                    onClick={doLogin}
                                    className="w-full transform rounded-lg bg-[#CF3688] px-6 py-3 text-base font-semibold text-white shadow-lg transition-all duration-200 hover:bg-[#A62B6D] hover:shadow-xl active:scale-[0.98] hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-50 sm:py-4 sm:text-lg"
                                >
                                    {getLanguageValue(PAGE_TYPE.COMMON, "login.btnLogin")}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            ) : null}
        </>
    );
};

export default LoginComponent;
